import { FC, useState, useEffect } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { Redirect } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import SplitIOTreatment from '../../components/splitio/splitioTreatment';
import AuthService from '../../services/auth/authService';
import Communication from '../components/Communication';
import SignInForm from '../components/SignInForm';
import OfflineBanner from '../components/OfflineBanner';
import { login } from '../../modules/user_api';
import logo from '../assets/restore_mini_logo.png';
import { theme } from '../theme';

const Content = styled.div`
  min-height: 100vh;
  display: flex;
  position: relative;
`;

const Section = styled.div(({ theme }) => {
  const { color } = theme;
  return `
    flex: none;
    grid-template-rows: repeat(5, minmax(0, 1fr));
    grid-template-columns: repeat(5, minmax(0, 1fr));
    width: 50%;
    min-height: 100%;
    display: grid;
    background: ${color.grayscale[1000]};

    @media (max-width: 940px) {
      width: 100%;
    }
  `;
});

const ContentPadding = styled.div`
  grid-row-end: 5;
  grid-row-start: 1;
  grid-column: span 1 / span 1;
`;

const TopContent = styled.div`
  align-content: center;
  flex-wrap: wrap;
  display: flex;
  grid-column: span 3 / span 3;
`;

const RightSection = styled.div(({ theme }) => {
  const { color } = theme;
  return `
    background-color: ${color.secondary.light};
    grid-template-columns: repeat(5, minmax(0, 1fr));
    flex-grow: 1;
    min-height: 100%;
    display: grid;
    @media (max-width: 940px) {
      width: 100%;
    }
  `;
});

const FormContainer = styled.div`
  grid-row-start: 2;
  grid-row: span 3 / span 3;
  grid-column: span 3 / span 3;
`;

const RestoreLogo = styled.img`
  width: 2.5rem;
  height: 2.5rem;
`;

const RightSectionPadding = styled.div`
  grid-row-end: 7;
  grid-row-start: 1;
  grid-column: span 1 / span 1;
`;

const CommunicationContainer = styled.div`
  grid-row-end: 6;
  grid-row-start: 2;
  grid-column: span 3 / span 3;
`;

export interface ISignIn {
  location: Location;
}

const SignIn: FC<ISignIn> = ({ location }) => {
  // TODO: Set up typing for redux store
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { userLoggedIn, authErrorMessage } = useSelector((state: any) => state.user, shallowEqual);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [offlineAvailable, setOfflineAvailable] = useState<boolean>(false);

  useEffect(() => {
    window.addEventListener('offline', () => {
      setOfflineAvailable(AuthService.getOfflineToken() !== null);
    });

    window.addEventListener('online', () => {
      setOfflineAvailable(false);
    });
  });

  useEffect(() => {
    if (authErrorMessage) {
      setLoading(false);
    }
  }, [loginAttempts, authErrorMessage]);

  const signIn = async (username: string, password: string) => {
    if (loading) {
      return;
    }
    setLoginAttempts(loginAttempts + 1);
    setLoading(true);
    localStorage.removeItem('logout.lock');
    dispatch(login(username, password));
  };

  if (userLoggedIn) {
    return (
      <Redirect
        to={{
          pathname: '/',
          state: { from: location },
        }}
      />
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Content>
        <Section>
          <ContentPadding />
          <TopContent>
            <RestoreLogo src={logo} alt="Restore"></RestoreLogo>
          </TopContent>

          <FormContainer>
            <div>
              <SplitIOTreatment featureName="DC-2275-offline-mode" treatmentValue="on">
                {(treatment: 'on' | 'off') => {
                  if (treatment === 'on') {
                    return (
                      <SignInForm
                        loading={loading}
                        errorMessage={authErrorMessage}
                        submitCallback={(username: string, password: string) => signIn(username, password)}
                        disabled={offlineAvailable || loading}
                      />
                    );
                  } else {
                    return (
                      <SignInForm
                        loading={loading}
                        errorMessage={authErrorMessage}
                        submitCallback={(username: string, password: string) => signIn(username, password)}
                        disabled={false || loading}
                      />
                    );
                  }
                }}
              </SplitIOTreatment>
            </div>
          </FormContainer>
        </Section>

        <RightSection>
          <RightSectionPadding />
          <CommunicationContainer>
            <Communication />
          </CommunicationContainer>
        </RightSection>

        <SplitIOTreatment featureName="DC-2275-offline-mode" treatmentValue="on">
          {(treatment: 'on' | 'off') => {
            if (treatment === 'on') {
              const offlineToken: string = AuthService.getOfflineToken() || '';
              return offlineAvailable && <OfflineBanner offlineToken={offlineToken} />;
            } else {
              return null;
            }
          }}
        </SplitIOTreatment>
      </Content>
    </ThemeProvider>
  );
};

export default SignIn;
